import { useState } from 'react';
import { format, isPast, isToday } from 'date-fns';
import { Bell, Clock, CheckCircle, Circle, Pencil, Trash2, Paperclip, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useReminderStore } from '@/store/reminderStore';
import { useToast } from '@/components/ui/use-toast';
import { ReminderFormDialog } from './ReminderFormDialog';
import { AttachmentSection } from '@/components/attachments/AttachmentSection';
import type { Reminder, LeadPriority } from '@/types/crm';

interface ReminderListProps {
  leadId?: string;
  dealId?: string;
  reminders: Reminder[];
}

const priorityColors: Record<LeadPriority, string> = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

export function ReminderList({ leadId, dealId, reminders }: ReminderListProps) {
  const { toggleReminderStatus, deleteReminder } = useReminderStore();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [expandedReminder, setExpandedReminder] = useState<string | null>(null);

  const handleToggleStatus = async (reminder: Reminder) => {
    try {
      await toggleReminderStatus(reminder.id, reminder.status);
      toast({
        title: reminder.status === 'pending' ? 'Reminder completed' : 'Reminder reopened',
      });
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this reminder?')) return;
    try {
      await deleteReminder(id);
      toast({ title: 'Reminder deleted' });
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' });
    }
  };

  const handleEdit = (reminder: Reminder) => {
    setEditingReminder(reminder);
    setShowForm(true);
  };

  const getDueDateStatus = (dueDate: string, status: string) => {
    if (status === 'done') return 'text-muted-foreground';
    const date = new Date(dueDate);
    if (isPast(date) && !isToday(date)) return 'text-red-600';
    if (isToday(date)) return 'text-orange-600';
    return 'text-muted-foreground';
  };

  const pendingReminders = reminders.filter(r => r.status === 'pending');
  const completedReminders = reminders.filter(r => r.status === 'done');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Bell className="h-4 w-4" />
          Reminders ({pendingReminders.length} pending)
        </h3>
        <Button size="sm" onClick={() => { setEditingReminder(null); setShowForm(true); }}>
          <Plus className="h-4 w-4 mr-1" />
          Add Reminder
        </Button>
      </div>

      {reminders.length === 0 ? (
        <p className="text-muted-foreground text-sm py-4 text-center">No reminders yet</p>
      ) : (
        <div className="space-y-3">
          {pendingReminders.map((reminder) => (
            <Card key={reminder.id} className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <button
                      onClick={() => handleToggleStatus(reminder)}
                      className="mt-0.5 text-muted-foreground hover:text-primary"
                    >
                      <Circle className="h-5 w-5" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{reminder.title}</span>
                        <Badge className={priorityColors[reminder.priority]} variant="secondary">
                          {reminder.priority}
                        </Badge>
                      </div>
                      <div className={`flex items-center gap-1 text-sm mt-1 ${getDueDateStatus(reminder.due_date, reminder.status)}`}>
                        <Clock className="h-3 w-3" />
                        {format(new Date(reminder.due_date), 'MMM d, yyyy h:mm a')}
                      </div>
                      {reminder.notes && (
                        <p className="text-sm text-muted-foreground mt-2">{reminder.notes}</p>
                      )}
                      <button
                        onClick={() => setExpandedReminder(expandedReminder === reminder.id ? null : reminder.id)}
                        className="text-xs text-primary hover:underline mt-2 flex items-center gap-1"
                      >
                        <Paperclip className="h-3 w-3" />
                        Attachments
                      </button>
                      {expandedReminder === reminder.id && (
                        <div className="mt-3 pt-3 border-t">
                          <AttachmentSection entityType="reminder" entityId={reminder.id} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(reminder)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(reminder.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {completedReminders.length > 0 && (
            <>
              <h4 className="text-sm font-medium text-muted-foreground mt-6">Completed</h4>
              {completedReminders.map((reminder) => (
                <Card key={reminder.id} className="border-l-4 border-l-green-500 opacity-60">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <button
                          onClick={() => handleToggleStatus(reminder)}
                          className="mt-0.5 text-green-600 hover:text-green-700"
                        >
                          <CheckCircle className="h-5 w-5" />
                        </button>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium line-through">{reminder.title}</span>
                          <div className="flex items-center gap-1 text-sm mt-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {format(new Date(reminder.due_date), 'MMM d, yyyy h:mm a')}
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(reminder.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      <ReminderFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        leadId={leadId}
        dealId={dealId}
        reminder={editingReminder}
      />
    </div>
  );
}
